import axios from "axios";
import SecureLS from 'secure-ls';
import { getPortalContext } from '../sso/portal-context';
import { markAuthenticationChanged } from '../sso/auth-runtime';
import { clearAuthenticationStorage, currentLocalToken } from '../sso/auth-storage';
const { resolveApiOrigin, resolveApplicationPath } = require('./api-origin');
const { rememberLoginReturnPath } = require('./login-return-path');

const secureStorage = new SecureLS({ encodingType: 'aes' });

// for production api url 
// const apiUrl() = "";
axios.interceptors.response.use(function (response) {
	return response;
}, function (error) {
	if (
		error.response
		&& error.response.status === 401
		&& !(error.config && error.config.ssoSkipUnauthorizedRedirect)
	) {
		const authorization = error.config && error.config.headers && error.config.headers.Authorization;
		const expectedToken = typeof authorization === 'string' && authorization.startsWith('Bearer ')
			? authorization.slice('Bearer '.length)
			: null;
		if (expectedToken === null || currentLocalToken() === expectedToken) {
			const context = getPortalContext();
			clearAuthenticationStorage(localStorage, secureStorage);
			if (context.enabled) markAuthenticationChanged();
			localStorage.setItem("session_expired", true);
			const loginPath = context.enabled
				? context.endpoints.login_path
				: resolveApplicationPath(process.env.VUE_APP_LEGACY_LOGIN_PATH, window.location.origin);
			// Signing in again returns to the page whose session was refused, not the role's default page.
			if (window.location.pathname !== loginPath) {
				rememberLoginReturnPath(
					window.sessionStorage,
					`${window.location.pathname}${window.location.search}${window.location.hash}`,
				);
			}
			window.location.assign(loginPath);
		}
	} else {
		return Promise.reject(error);
	}
});

const ApiService = {
	baseUrl: resolveApiOrigin(process.env.VUE_APP_API_BASE_URL, window.location.origin),
	get apiUrl() { return this.baseUrl + '/api/' },
	get imageUrl() { return this.baseUrl + '/storage' },

	authHeaders() {
		return {
			"Access-Control-Allow-Headers": "Origin, Authorization, Accept",
			"Access-Control-Allow-Origin": this.baseUrl,
			"Authorization": `Bearer ${localStorage.getItem("token")}`,
			"Accept": "application/json"
		};
	},
	post(resource, params, auth = false) {
		let headers = this.authHeaders();
		return axios.post(this.apiUrl + `${resource}`, params, { headers: headers });
	},
	get(resource, params, auth = false) {
		let headers = this.authHeaders();
		return axios.get(this.apiUrl + `${resource}`, {
			params: params,
			headers: headers
		});
	},
	put(resource, params, auth = false) {
		let headers = this.authHeaders();
		return axios.put(this.apiUrl + `${resource}`, params, { headers: headers });
	},
	delete(resource, params, auth = false) {
		let headers = this.authHeaders();
		return axios.delete(this.apiUrl + `${resource}`, params, { headers: headers });
	},
	fileUpload(resource, params, auth = false) {
		return axios.post(this.apiUrl + `${resource}`, params, {
			headers: {
				...this.authHeaders(),
				"Access-Control-Allow-Headers": "Origin, Content-Type, Authorization, Accept",
				"Content-Type": "multipart/form-data",
			}
		});
	},
	setHeader() {
		let token = localStorage.getItem("userToken");
		axios.defaults.headers.common.Authorization = token;
	},
	importfileUpload(resource, params, auth = false) {
		ApiService.setHeader();
		return axios.post(this.apiUrl + `${resource}`, params, {
			headers: {
				...this.authHeaders(),
				"Access-Control-Allow-Headers": "Origin, Content-Type, Authorization, Accept",
				"Content-Type": "multipart/form-data"
			}
		});
	},
	downloadFile(resource, fileName, params, auth = false) {
		let headers = this.authHeaders();
		var FileSaver = require('file-saver');
		return axios.post(this.apiUrl + `${resource}`, params, {
			responseType: 'blob',
			headers: headers
		}).then(resp => {
			FileSaver.saveAs(resp.data, fileName);
		});
	},
};

export default ApiService;
